/**
 * Esqueletos de carregamento.
 *
 * Substituem o spinner "Carregando…" que as telas de módulo usavam. A troca não
 * é cosmética: o spinner não tem tamanho, então a tela PULA quando os dados
 * chegam — uma linha de texto vira uma tabela de 400px de altura. O esqueleto
 * ocupa o espaço que o conteúdo vai ocupar, e o layout final é desenhado uma vez
 * só.
 *
 * ⚠️ **Nenhum valor de design é declarado aqui**, como em `primitivos.tsx`. Cor,
 * raio e espaçamento vêm dos tokens pelos utilitários do Tailwind. O cinza é
 * `border-subtle` — o mesmo das bordas de tabela e card — e não um cinza novo:
 * ele é semântico, então segue `[data-theme="dark"]` sozinho no dia em que o
 * tema escuro for ligado.
 *
 * ♿ **`rotulo` é o que decide se há anúncio.** Com rótulo, o esqueleto se
 * embrulha em UM `role="status"` que anuncia "Carregando clientes…" — o mesmo
 * que o spinner fazia. Sem rótulo, ele sai mudo, para as telas que compõem dois
 * blocos (gráfico + tabela) dentro de uma `RegiaoDeCarregamento` só: dois
 * `role="status"` simultâneos na mesma tela se atropelam no anúncio.
 *
 * Nenhum esqueleto usa `<table>`/`<th>` de verdade pelo mesmo motivo: uma tabela
 * sem cabeçalho real é ruído para leitor de tela. A aparência de tabela sai de
 * `grid`, que não tem semântica.
 *
 * 🎞️ `animate-pulse` (2s, opacidade) com `motion-reduce:animate-none`. Sem
 * shimmer varrendo: ele precisa de keyframes próprios e de uma cor de brilho que
 * o design system não define.
 */
import type { ReactNode } from 'react'

/** O cinza pulsante. Todo o resto deste arquivo é composição dele. */
export function Bloco({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`bg-border-subtle rounded-control block animate-pulse motion-reduce:animate-none ${className}`}
    />
  )
}

/**
 * A casca acessível de um esqueleto.
 *
 * `aria-busy` diz que a região está incompleta; `aria-live="polite"` faz o
 * rótulo ser anunciado sem interromper o que o usuário estiver ouvindo.
 *
 * Exportada para as telas que empilham dois blocos sob um anúncio só — ver
 * `CostExplorer` e `DashboardOrcamento`.
 */
export function RegiaoDeCarregamento({
  rotulo,
  className = '',
  children,
}: {
  rotulo: string
  className?: string
  children: ReactNode
}) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{rotulo}</span>
      {children}
    </div>
  )
}

/**
 * Embrulha o esqueleto no anúncio quando há rótulo, e o devolve cru quando não
 * há. É o que faz `rotulo` opcional significar "este bloco já está dentro de uma
 * região" em vez de "este bloco é inacessível".
 */
function comAnuncio(rotulo: string | undefined, corpo: ReactNode) {
  if (!rotulo) return corpo
  return <RegiaoDeCarregamento rotulo={rotulo}>{corpo}</RegiaoDeCarregamento>
}

/**
 * Larguras das barras de texto, em ciclo.
 *
 * Existe para o esqueleto não virar uma grade perfeita de retângulos idênticos,
 * que lê como componente quebrado e não como texto ainda por chegar. Ciclo fixo,
 * e não `Math.random()`: aleatório muda a cada render e faz as barras saltitarem
 * enquanto a consulta corre.
 */
const LARGURAS = ['w-4/5', 'w-3/5', 'w-full', 'w-2/3', 'w-1/2', 'w-3/4'] as const

const largura = (indice: number) => LARGURAS[indice % LARGURAS.length]

/**
 * A tabela dentro do card com borda — a forma de 8 das telas de módulo.
 *
 * `colunas` é uma CONTAGEM, e não os rótulos reais: passar os rótulos obrigaria
 * cada tela a extrair seu array de cabeçalho para uma const compartilhada só por
 * causa do esqueleto, e um cabeçalho desenhado antes dos dados não fica mais
 * verdadeiro — só mais barulhento.
 */
export function EsqueletoDeTabela({
  rotulo,
  colunas = 4,
  linhas = 5,
  comBorda = true,
  className = '',
}: {
  rotulo?: string
  colunas?: number
  linhas?: number
  /** `false` para a tabela que já vive dentro de um card — a de lançamentos da `2j`. */
  comBorda?: boolean
  className?: string
}) {
  const grade = { gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }
  const respiro = comBorda ? 'px-5' : ''

  return comAnuncio(
    rotulo,
    <div
      aria-hidden="true"
      className={`${
        comBorda ? 'border-border-subtle bg-surface-card rounded-card overflow-hidden border' : ''
      } ${className}`}
    >
      <div className={`border-border-subtle grid gap-4 border-b py-3.5 ${respiro}`} style={grade}>
        {Array.from({ length: colunas }, (_, coluna) => (
          <Bloco key={coluna} className="h-3 w-1/2" />
        ))}
      </div>

      {Array.from({ length: linhas }, (_, linha) => (
        <div
          key={linha}
          className={`border-border-subtle grid gap-4 border-b py-4 last:border-b-0 ${respiro}`}
          style={grade}
        >
          {Array.from({ length: colunas }, (_, coluna) => (
            <Bloco key={coluna} className={`h-4 ${largura(linha + coluna)}`} />
          ))}
        </div>
      ))}
    </div>,
  )
}

/** A fileira de `MetricCard` das telas `2g` / `2h`, na altura mínima dos cards reais. */
export function EsqueletoDeCards({
  rotulo,
  quantidade = 3,
  colunas = 'lg:grid-cols-3',
  className = '',
}: {
  rotulo?: string
  quantidade?: number
  /** A classe de grade da própria tela — o esqueleto quebra onde os cards reais quebram. */
  colunas?: string
  className?: string
}) {
  return comAnuncio(
    rotulo,
    <div className={`grid gap-4 ${colunas} ${className}`} aria-hidden="true">
      {Array.from({ length: quantidade }, (_, indice) => (
        <div
          key={indice}
          className="border-border-subtle bg-surface-card rounded-card flex min-h-[110px] flex-col justify-between border p-5"
        >
          <Bloco className="h-2.5 w-1/2" />
          <Bloco className="h-6 w-2/3" />
          <Bloco className="h-2.5 w-1/3" />
        </div>
      ))}
    </div>,
  )
}

/**
 * Alturas das barras do gráfico.
 *
 * Barras de alturas diferentes, e não um retângulo cinza: o retângulo lê como
 * imagem que falhou ao carregar. Fixas pelo mesmo motivo das larguras acima.
 */
const ALTURAS_DE_BARRA = [
  'h-1/3',
  'h-2/3',
  'h-1/2',
  'h-5/6',
  'h-2/5',
  'h-3/4',
  'h-full',
  'h-1/2',
  'h-3/5',
  'h-4/5',
  'h-1/3',
  'h-2/3',
] as const

/** O lugar do gráfico Recharts, na altura que a tela reserva para o `ResponsiveContainer`. */
export function EsqueletoDeGrafico({
  rotulo,
  altura = 'h-72',
  comCabecalho = true,
  comBorda = true,
  className = '',
}: {
  rotulo?: string
  /** A mesma classe de altura que a tela dá ao `ResponsiveContainer`. */
  altura?: string
  /** O título e o subtítulo acima do gráfico, como na `2c`. */
  comCabecalho?: boolean
  /** `false` para o gráfico que já vive dentro de um painel — o da `2g`. */
  comBorda?: boolean
  className?: string
}) {
  return comAnuncio(
    rotulo,
    <div
      aria-hidden="true"
      className={`flex flex-col gap-1 ${
        comBorda ? 'border-border-subtle bg-surface-card rounded-card border p-5' : ''
      } ${className}`}
    >
      {comCabecalho && (
        <>
          <Bloco className="h-5 w-1/3" />
          <Bloco className="mt-2 h-3 w-1/2" />
        </>
      )}

      <div className={`mt-4 flex w-full items-end gap-3 ${altura}`}>
        {ALTURAS_DE_BARRA.map((alturaDaBarra, indice) => (
          <Bloco key={indice} className={`rounded-field flex-1 ${alturaDaBarra}`} />
        ))}
      </div>

      {/* O eixo X. Sem ele as barras flutuam e a forma não lê como gráfico. */}
      <Bloco className="mt-3 h-1.5 w-full" />
    </div>,
  )
}

/**
 * Uma lista de itens selecionáveis.
 *
 * `linhas` é a coluna mestre de `Faturas` e o acordeão de `Perfis` — itens
 * separados por borda dentro de um card só. `cartoes` é a coluna de `Contratos`
 * e a lista de aditivos — cards soltos com espaço entre eles.
 */
export function EsqueletoDeLista({
  rotulo,
  itens = 4,
  variante = 'linhas',
  className = '',
}: {
  rotulo?: string
  itens?: number
  variante?: 'linhas' | 'cartoes'
  className?: string
}) {
  const item = (indice: number) => (
    <div
      key={indice}
      className={
        variante === 'linhas'
          ? 'border-border-subtle flex items-center justify-between gap-3 border-b px-5 py-4 last:border-b-0'
          : 'border-border-subtle bg-surface-card rounded-card flex items-center justify-between gap-3 border px-5 py-4'
      }
    >
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <Bloco className={`h-4 ${largura(indice)}`} />
        <Bloco className="h-3 w-1/3" />
      </span>
      <Bloco className="h-6 w-20 shrink-0" />
    </div>
  )

  return comAnuncio(
    rotulo,
    <div
      aria-hidden="true"
      className={`${
        variante === 'linhas'
          ? 'border-border-subtle bg-surface-card rounded-card overflow-hidden border'
          : 'flex flex-col gap-3'
      } ${className}`}
    >
      {Array.from({ length: itens }, (_, indice) => item(indice))}
    </div>,
  )
}

/** Larguras dos chips de catálogo — pílulas de tamanhos diferentes numa linha que quebra. */
const LARGURAS_DE_CHIP = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-24'] as const

/** Os chips das listas globais da Administração (status de oportunidade, serviços AWS). */
export function EsqueletoDeChips({
  rotulo,
  itens = 5,
  className = '',
}: {
  rotulo?: string
  itens?: number
  className?: string
}) {
  return comAnuncio(
    rotulo,
    <div className={`flex flex-wrap items-center gap-2 ${className}`} aria-hidden="true">
      {Array.from({ length: itens }, (_, indice) => (
        <Bloco
          key={indice}
          className={`h-7 ${LARGURAS_DE_CHIP[indice % LARGURAS_DE_CHIP.length]}`}
        />
      ))}
    </div>,
  )
}

/**
 * O card de formulário da `2f` — cabeçalho, linhas de campos e a barra de ações.
 *
 * Também é o painel de detalhe de `Contratos` e de `Faturas`: os três são um
 * card de campos rotulados, e a diferença entre eles é só quantas linhas.
 *
 * `linhas` conta LINHAS do grid, e não campos: são três campos por linha, e é a
 * linha que ocupa a altura.
 */
export function EsqueletoDeFormulario({
  rotulo,
  linhas = 4,
  className = '',
}: {
  rotulo?: string
  linhas?: number
  className?: string
}) {
  return comAnuncio(
    rotulo,
    <div
      aria-hidden="true"
      className={`border-border-subtle bg-surface-card rounded-card flex w-full max-w-[760px] flex-col gap-6 border p-7 ${className}`}
    >
      <div className="flex items-start justify-between gap-4">
        <Bloco className="h-6 w-52" />
        <Bloco className="h-4 w-40" />
      </div>

      {Array.from({ length: linhas }, (_, linha) => (
        <div key={linha} className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }, (_, campo) => (
            <span key={campo} className="flex flex-col gap-1.5">
              <Bloco className="h-3 w-1/2" />
              <Bloco className="rounded-field h-9 w-full" />
            </span>
          ))}
        </div>
      ))}

      <div className="border-border-subtle flex items-center gap-2 border-t pt-5">
        <Bloco className="h-9 w-32" />
        <Bloco className="h-9 w-24" />
      </div>
    </div>,
  )
}

/**
 * O fallback do `<Suspense>` do `ModuleFrame`, enquanto o chunk `lazy` da tela baixa.
 *
 * Genérico de propósito: o frame não sabe qual rota está entrando. Título, uma
 * fileira de cards e uma tabela — a forma da maioria das telas de módulo, e que
 * erra pouco nas outras.
 */
export function EsqueletoDeTela({ rotulo = 'Carregando tela…' }: { rotulo?: string }) {
  return (
    <RegiaoDeCarregamento rotulo={rotulo} className="flex flex-col gap-5">
      <Bloco className="h-6 w-56" />
      <EsqueletoDeCards />
      <EsqueletoDeTabela />
    </RegiaoDeCarregamento>
  )
}
